import { useEffect, useState } from "react";

import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";
import { Skeleton } from "primereact/skeleton";
import { TabPanel, TabView } from "primereact/tabview";
import { getCweTags } from "sj-ui-toolkit/src/api/cweTags";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import AspenAdaptCWEs from "@/components/aspen/AspenAdaptCWEs";
import AspenAdaptTraining from "@/components/aspen/AspenAdaptTraining";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";

interface CweDescriptions {
  [key: string]: { Name?: string; Description?: string };
}

export default function AspenAdapt() {
  useBreadcrumb({
    label: "Aspen Adapt",
    route: AdminAppRoutes.routes.aspen.routes.adapt.route,
    display: "none",
    resetTrail: true,
  });

  const { features, isLoading, viewOnly, currentTenantName, toggleFeature } = useAspenFeatures();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [cweDescriptions, setCweDescriptions] = useState<CweDescriptions | null>(null);

  useEffect(() => {
    const loadCweDescriptions = async () => {
      try {
        const data = await getCweTags();
        setCweDescriptions(data);
      } catch {
        setCweDescriptions(null);
      }
    };

    void loadCweDescriptions();
  }, []);

  if (isLoading) return <Skeleton width="100%" height="20rem" />;

  return (
    <>
      <h1 className="mb-4">Aspen Adapt</h1>

      <TabView activeIndex={activeTabIndex} onTabChange={e => setActiveTabIndex(e.index)}>
        {features.adapt && (
          <TabPanel header="Overview">
            <AspenAdaptCWEs
              tenant={currentTenantName}
              showRepository={features.recordRepoName}
              showUsername={features.recordUsername}
              showPrNumber={features.recordPrNumber}
            />
          </TabPanel>
        )}

        {features.adapt && (
          <TabPanel header="Training">
            <AspenAdaptTraining tenant={currentTenantName} cweDescriptions={cweDescriptions} />
          </TabPanel>
        )}

        <TabPanel header="Adapt Settings">
          <Card pt={{ content: { className: "p-0" } }} className="mb-4">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-surface-200">
              <strong>Aspen Adapt</strong>
              <InputSwitch
                checked={features.adapt}
                onChange={e => void toggleFeature("adapt", e.value)}
                disabled={viewOnly}
              />
            </div>
            <p>
              Aspen Adapt enables you to assign hyper-relevant content to your team based on their
              developer activity. With the Security Journey API, and 3rd party integrations (such as
              GitHub Actions), you can view CWEs committed by your developers so you can assign
              tailored training.
            </p>
            <a
              href="https://github.com/securityJourney/aspen-connector"
              className={`block mt-4 ${themeClasses.textPrimaryDarker}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Connect your GitHub workflows to Aspen Adapt
            </a>
          </Card>

          <Card pt={{ content: { className: "p-0" } }} className="mb-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <strong>Record Repository Name</strong>
                  <p className="body-sm text-secondary">
                    Identify what git repository a vulnerability was committed in
                  </p>
                </div>
                <InputSwitch
                  checked={features.recordRepoName}
                  onChange={e => void toggleFeature("recordRepoName", e.value)}
                  disabled={viewOnly}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Record Username</strong>
                  <p className="body-sm text-secondary">
                    Identify user by GitHub username, which may differ from email
                  </p>
                </div>
                <InputSwitch
                  checked={features.recordUsername}
                  onChange={e => void toggleFeature("recordUsername", e.value)}
                  disabled={viewOnly}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Record PR Number</strong>
                  <p className="body-sm text-secondary">
                    Identify what pull request a vulnerability was committed in
                  </p>
                </div>
                <InputSwitch
                  checked={features.recordPrNumber}
                  onChange={e => void toggleFeature("recordPrNumber", e.value)}
                  disabled={viewOnly}
                />
              </div>
            </div>
          </Card>
        </TabPanel>
      </TabView>
    </>
  );
}
